import { useState, useEffect } from 'react';
import { Video, ChevronDown, ChevronUp, RotateCcw, X, Monitor, Smartphone, Tv, Zap, Save, Check } from 'lucide-react';
import { showToast } from '@/lib/toast';
import { VideoSettings } from '@/types';

interface VideoSettingsPanelProps {
  settings: VideoSettings;
  onUpdateSettings: (key: keyof VideoSettings, value: any) => void;
  onClose?: () => void;
  onSave?: () => void;
}

const videoPresets = [
  { name: 'YouTube 1080p', icon: Monitor, resolution: '1920x1080', quality: 'high' as const, format: 'mp4' },
  { name: 'Instagram Story', icon: Smartphone, resolution: '1080x1920', quality: 'medium' as const, format: 'mp4' },
  { name: 'TikTok/Reels', icon: Smartphone, resolution: '1080x1920', quality: 'medium' as const, format: 'mp4' },
  { name: 'YouTube 4K', icon: Tv, resolution: '3840x2160', quality: 'ultra' as const, format: 'mp4' },
  { name: 'Web Otimizado', icon: Monitor, resolution: '1280x720', quality: 'medium' as const, format: 'webm' },
  { name: 'Compacto', icon: Smartphone, resolution: '854x480', quality: 'low' as const, format: 'mp4' },
];

const videoFormats = [
  { value: 'mp4', label: 'MP4', description: 'Formato mais compatível' },
  { value: 'webm', label: 'WebM', description: 'Otimizado para web' },
  { value: 'mov', label: 'MOV', description: 'QuickTime, alta qualidade' },
];

const qualityOptions = [
  { value: 'low', label: 'Baixa', description: 'Menor tamanho' },
  { value: 'medium', label: 'Média', description: 'Equilibrio qualidade/tamanho' },
  { value: 'high', label: 'Alta', description: 'Boa qualidade' },
  { value: 'ultra', label: 'Ultra', description: 'Máxima qualidade' }
];

// Adicionar novas opções de compressão
const compressionPresets = [
  { value: 'light', label: 'Leve', description: 'Redução de 25% no tamanho', bitrate: '8000k', crf: 23 },
  { value: 'medium', label: 'Médio', description: 'Redução de 50% no tamanho', bitrate: '5000k', crf: 26 },
  { value: 'high', label: 'Alto', description: 'Redução de 75% no tamanho', bitrate: '2500k', crf: 29 },
  { value: 'custom', label: 'Personalizado', description: 'Configuração manual', bitrate: 'custom', crf: 'custom' }
];

const videoCodecs = [
  { value: 'h264', label: 'H.264', description: 'Mais compatível' },
  { value: 'h265', label: 'H.265/HEVC', description: 'Melhor compressão' },
  { value: 'vp9', label: 'VP9', description: 'Ideal para web' }
];

export default function VideoSettingsPanel({ settings, onUpdateSettings, onClose, onSave }: VideoSettingsPanelProps) {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [savedSettings, setSavedSettings] = useState<VideoSettings | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [compressionLevel, setCompressionLevel] = useState('light');
  const [customBitrate, setCustomBitrate] = useState('5000');
  const [customCRF, setCustomCRF] = useState('23');
  const [selectedCodec, setSelectedCodec] = useState('h264');

  const applyPreset = (preset: typeof videoPresets[0]) => {
    onUpdateSettings('format', preset.format);
    onUpdateSettings('quality', preset.quality);
    onUpdateSettings('resolution', preset.resolution);
    setSelectedPreset(preset.name);
  };

  const resetToDefaults = () => {
    onUpdateSettings('format', 'mp4');
    onUpdateSettings('quality', 'medium');
    onUpdateSettings('resolution', 'original');
    setSelectedPreset(null);
  };

  // Load saved settings once
  useEffect(() => {
    const saved = localStorage.getItem('converterPro_videoSettings');
    if (saved) {
      try {
        setSavedSettings(JSON.parse(saved));
      } catch (error) {
        console.error('Erro ao analisar configurações salvas de vídeo:', error);
      }
    } else {
      setSavedSettings({ ...settings });
    }
  }, []);

  // Track changes
  useEffect(() => {
    if (savedSettings) {
      const changed = Object.keys(settings).some((key) => {
        const curr = settings[key as keyof VideoSettings];
        const savedVal = savedSettings[key as keyof VideoSettings];
        return JSON.stringify(curr) !== JSON.stringify(savedVal);
      });
      setHasUnsavedChanges(changed);
    }
  }, [settings, savedSettings]);

  const handleSave = () => {
    try {
      localStorage.setItem('converterPro_videoSettings', JSON.stringify(settings));
      setSavedSettings({ ...settings });
      setHasUnsavedChanges(false);
      showToast('✅ Configurações salvas e aplicadas');
      if (onSave) onSave();
      if (onClose) onClose();
    } catch (e) {
      console.error('Erro ao salvar configurações de vídeo:', e);
    }
  };

  const isDarkMode = document.documentElement.classList.contains('dark');

  return (
    <div className={`${
      isDarkMode ? 'bg-[#383D52] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
    } border rounded-2xl overflow-hidden shadow-xl w-full max-h-[90vh] overflow-y-auto`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 p-3 sm:p-4 border-b ${
        isDarkMode ? 'bg-[#383D52] border-[#4A4F64]' : 'bg-white border-[#E5E5E5]'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
              <Video className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className={`text-base sm:text-lg font-bold ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Configurações de Vídeo
              </h3>
              <p className={`text-xs ${
                isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
              }`}>
                Personalize suas conversões de vídeo
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className={`flex-1 sm:flex-none flex items-center justify-center space-x-1.5 px-3 py-2 rounded-lg transition-all duration-300 ${
                hasUnsavedChanges
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg'
                  : isDarkMode
                    ? 'bg-[#4A4F64] text-[#EAECF3]/50 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              <span className="text-sm font-medium">Salvar</span>
              {hasUnsavedChanges && (
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              )}
            </button>

            <button
              onClick={resetToDefaults}
              className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                isDarkMode ? 'bg-[#4A4F64] hover:bg-[#52576B] text-[#EAECF3]' : 'bg-[#F0F0F0] hover:bg-[#E5E5E5] text-[#4A4A69]'
              } transition-colors`}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setIsAdvancedMode(!isAdvancedMode)}
              className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-lg ${
                isAdvancedMode
                  ? 'bg-red-500/20 text-red-300 border border-red-400/30'
                  : isDarkMode ? 'bg-[#4A4F64] hover:bg-[#52576B] text-[#EAECF3]' : 'bg-[#F0F0F0] hover:bg-[#E5E5E5] text-[#4A4A69]'
              } transition-colors`}
            >
              <span className="text-sm font-medium sm:hidden">Avançado</span>
              {isAdvancedMode ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className={`p-1.5 sm:p-2 rounded-lg ${
                  isDarkMode ? 'hover:bg-red-500/20 text-red-300' : 'hover:bg-red-50 text-red-600'
                } transition-colors`}
              >
                <X className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Toast handled globally, banner removed */}
      </div>
      
      <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
        {/* Presets Rápidos */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Zap className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-red-500" />
            <h4 className={`font-semibold text-xs sm:text-sm ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Presets Rápidos
            </h4>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {videoPresets.map((preset, index) => {
              const IconComponent = preset.icon;
              const isSelected = selectedPreset === preset.name;
              
              return (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className={`group relative p-2 sm:p-3 rounded-lg border transition-all duration-500 hover:scale-[1.02] hover:shadow-lg text-left ${
                    isSelected
                      ? 'bg-red-500 border-red-400 text-white shadow-lg shadow-red-500/25'
                      : (isDarkMode ? 'bg-[#2A2E3F] border-[#4A4F64] hover:bg-[#383D52] text-[#EAECF3] hover:border-red-400/50' : 'bg-[#F8F9FA] border-[#E5E5E5] hover:bg-white text-[#4A4A69] hover:border-red-400/50')
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-3.5 sm:w-4 h-3.5 sm:h-4 bg-white rounded-full flex items-center justify-center z-10">
                      <Check className="w-2 sm:w-2.5 h-2 sm:h-2.5 text-red-500" />
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1.5 sm:mb-2">
                    <IconComponent className={`w-3.5 sm:w-4 h-3.5 sm:h-4 transition-all duration-300 ${
                      isSelected 
                        ? 'text-white' 
                        : 'text-current group-hover:scale-110'
                    }`} />
                    <span className={`font-medium text-xs sm:text-sm transition-colors duration-300 ${
                      isSelected ? 'text-white' : 'text-current'
                    }`}>{preset.name}</span>
                  </div>
                  <div className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                    isSelected ? 'text-red-100' : isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                  }`}>
                    {preset.format.toUpperCase()} • {preset.quality}
                  </div>
                  
                  {isSelected && (
                    <>
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-red-400 to-pink-400 opacity-20" />
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-red-300 to-pink-300 opacity-5" />
                    </>
                  )}
                  
                  <div className={`absolute inset-0 rounded-lg bg-gradient-to-r from-red-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                    isSelected ? 'hidden' : ''
                  }`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Configurações Básicas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Formato
            </label>
            <select
              value={settings.format}
              onChange={(e) => onUpdateSettings('format', e.target.value)}
              className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border ${
                isDarkMode 
                  ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                  : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
              }`}
            >
              {videoFormats.map(format => (
                <option key={format.value} value={format.value}>
                  {format.label} - {format.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
              isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
            }`}>
              Qualidade
            </label>
            <select
              value={settings.quality}
              onChange={(e) => onUpdateSettings('quality', e.target.value)}
              className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border ${
                isDarkMode 
                  ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                  : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
              }`}
            >
              {qualityOptions.map(quality => (
                <option key={quality.value} value={quality.value}>
                  {quality.label} - {quality.description}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Adicionar a seção de configurações avançadas */}
      {isAdvancedMode && (
        <div className="p-3 sm:p-4 border-t border-[#4A4F64] dark:border-[#2A2E3F]">
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-3">
              <Zap className="w-4 h-4 text-red-500" />
              <h4 className={`font-semibold text-sm ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Compressão Avançada
              </h4>
            </div>

            {/* Nível de Compressão */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Nível de Compressão
              </label>
              <select
                value={compressionLevel}
                onChange={(e) => {
                  setCompressionLevel(e.target.value);
                  if (e.target.value !== 'custom') {
                    const preset = compressionPresets.find(p => p.value === e.target.value);
                    if (preset) {
                      setCustomBitrate(preset.bitrate.replace('k', ''));
                      setCustomCRF(preset.crf.toString());
                    }
                  }
                }}
                className={`w-full px-3 py-2 text-sm rounded-lg border ${
                  isDarkMode 
                    ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                    : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                }`}
              >
                {compressionPresets.map(preset => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label} - {preset.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Codec de Vídeo */}
            <div>
              <label className={`block text-xs font-medium mb-2 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                Codec de Vídeo
              </label>
              <select
                value={selectedCodec}
                onChange={(e) => setSelectedCodec(e.target.value)}
                className={`w-full px-3 py-2 text-sm rounded-lg border ${
                  isDarkMode 
                    ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                    : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                }`}
              >
                {videoCodecs.map(codec => (
                  <option key={codec.value} value={codec.value}>
                    {codec.label} - {codec.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Configurações Personalizadas */}
            {compressionLevel === 'custom' && (
              <div className="space-y-4">
                <div>
                  <label className={`block text-xs font-medium mb-2 ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    Bitrate (kbps)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={customBitrate}
                      onChange={(e) => setCustomBitrate(e.target.value)}
                      min="500"
                      max="50000"
                      step="500"
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                        isDarkMode 
                          ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                          : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                      }`}
                    />
                    <span className={`text-xs ${
                      isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                    }`}>
                      kbps
                    </span>
                  </div>
                  <p className={`mt-1 text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                  }`}>
                    Recomendado: 2000-8000 para 1080p
                  </p>
                </div>

                <div>
                  <label className={`block text-xs font-medium mb-2 ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    Fator de Taxa Constante (CRF)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={customCRF}
                      onChange={(e) => setCustomCRF(e.target.value)}
                      min="17"
                      max="35"
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                        isDarkMode 
                          ? 'bg-[#2A2E3F] border-[#4A4F64] text-[#EAECF3]' 
                          : 'bg-white border-[#E5E5E5] text-[#4A4A69]'
                      }`}
                    />
                  </div>
                  <p className={`mt-1 text-xs ${
                    isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
                  }`}>
                    17-23: Alta qualidade | 24-28: Boa qualidade | 29-35: Compressão máxima
                  </p>
                </div>
              </div>
            )}

            {/* Dicas de Compressão */}
            <div className={`mt-4 p-3 rounded-lg ${
              isDarkMode ? 'bg-[#2A2E3F]' : 'bg-gray-50'
            }`}>
              <h5 className={`text-xs font-medium mb-2 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                💡 Dicas de Compressão
              </h5>
              <ul className={`text-xs space-y-1 ${
                isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
              }`}>
                <li>• H.265 oferece melhor compressão mas pode ser mais lento</li>
                <li>• Valores CRF menores = melhor qualidade, arquivo maior</li>
                <li>• Para streaming, mantenha o bitrate abaixo de 5000kbps</li>
                <li>• VP9 é ideal para vídeos destinados à web</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 